import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CurrentTenantResponseSchema,
  MeResponseSchema,
  isFeatureOnPlatform,
  type CurrentTenantResponse,
  type FeatureKey,
  type MeResponse,
  type MembershipSummary,
  type Resource,
  type WalletSummary,
} from '@growhero/shared';
import { useAuth } from '@/auth/AuthProvider';
import { api, isApiError, setApiTenantId } from '@/lib/api';
import { setDisplayTimeZone } from '@/lib/format';

const STORAGE_KEY = 'gh.tenantId';

export type TenantStatus = 'idle' | 'loading' | 'ready' | 'noTenant' | 'error';

export interface TenantContextValue {
  status: TenantStatus;
  me: MeResponse | null;
  memberships: MembershipSummary[];
  /** Selected tenant id (also sent as X-Tenant-Id). */
  tenantId: string | null;
  current: CurrentTenantResponse | null;
  features: ReadonlySet<FeatureKey>;
  /** Plan includes the feature (and it is available on this deployment). */
  hasFeature: (key: FeatureKey) => boolean;
  /**
   * The feature exists on this deployment. False only for a platform-switched feature the server
   * has not turned on (GET /me `platformFeatures`, e.g. `google.business` behind the API's
   * GOOGLE_BUSINESS_ENABLED). Such a feature is hidden entirely: no nav, route or upsell.
   */
  isOnPlatform: (key: FeatureKey) => boolean;
  isPro: boolean;
  isPlatformAdmin: boolean;
  /** active or past_due (grace) subscriptions can use the portal. */
  subscriptionActive: boolean;
  wallet: (r: Resource) => WalletSummary | undefined;
  switchTenant: (id: string) => void;
  refresh: () => Promise<void>;
  error: unknown;
}

export const TenantContext = createContext<TenantContextValue | null>(null);

function readStored() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function pickTenant(me: MeResponse, preferred: string | null) {
  const usable = me.memberships.filter((m) => m.status === 'active');
  return (usable.find((m) => m.tenantId === preferred) ?? usable[0])?.tenantId ?? null;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const { status: authStatus, user } = useAuth();
  const qc = useQueryClient();
  const [preferred, setPreferred] = useState<string | null>(() => readStored());
  const signedIn = authStatus === 'signedIn';

  const meQ = useQuery({
    queryKey: ['me', user?.uid],
    queryFn: ({ signal }) => api.get('/me', { schema: MeResponseSchema, noTenant: true, signal }),
    enabled: signedIn,
    staleTime: 60_000,
  });

  const tenantId = meQ.data ? pickTenant(meQ.data, preferred) : null;
  // Keep the API client in sync *before* children issue tenant-scoped requests.
  setApiTenantId(tenantId);

  const currentQ = useQuery({
    queryKey: ['tenant-current', tenantId],
    queryFn: ({ signal }) =>
      api.get('/tenants/current', { schema: CurrentTenantResponseSchema, signal }),
    enabled: signedIn && !!tenantId,
    staleTime: 30_000,
  });

  // Dates/times follow the tenant's timezone; set before children render (like the tenant id).
  setDisplayTimeZone(currentQ.data?.tenant.timezone);

  useEffect(() => {
    if (!tenantId) return;
    try {
      localStorage.setItem(STORAGE_KEY, tenantId);
    } catch {
      /* private mode */
    }
  }, [tenantId]);

  const switchTenant = useCallback(
    (id: string) => {
      setPreferred(id);
      setApiTenantId(id);
      // Tenant-scoped caches are keyed by tenant id, but drop them anyway to free memory.
      qc.removeQueries({ predicate: (q) => q.queryKey[0] === 't' });
    },
    [qc],
  );

  const refresh = useCallback(async () => {
    await Promise.all([meQ.refetch(), currentQ.refetch()]);
  }, [meQ, currentQ]);

  const value = useMemo<TenantContextValue>(() => {
    const platform = meQ.data?.platformFeatures ?? [];
    const isOnPlatform = (k: FeatureKey) => isFeatureOnPlatform(k, platform);
    const features = new Set<FeatureKey>((currentQ.data?.features ?? []).filter(isOnPlatform));
    let status: TenantStatus = 'idle';
    if (signedIn) {
      if (meQ.isPending || (tenantId && currentQ.isPending)) status = 'loading';
      else if (meQ.isError || currentQ.isError) status = 'error';
      else if (!tenantId) status = 'noTenant';
      else status = 'ready';
    }
    // A stale stored id the user no longer belongs to → the API answers 404; fall back.
    if (
      isApiError(currentQ.error) &&
      currentQ.error.status === 404 &&
      meQ.data &&
      meQ.data.memberships.length
    ) {
      status = 'loading';
    }
    const tstatus = currentQ.data?.tenant.status;
    return {
      status,
      me: meQ.data ?? null,
      memberships: meQ.data?.memberships ?? [],
      tenantId,
      current: currentQ.data ?? null,
      features,
      hasFeature: (k) => features.has(k),
      isOnPlatform,
      isPro: currentQ.data?.plan?.key === 'pro',
      isPlatformAdmin: !!meQ.data?.platformAdmin,
      subscriptionActive: tstatus === 'active' || tstatus === 'past_due',
      wallet: (r) => currentQ.data?.wallets.find((w) => w.resource === r),
      switchTenant,
      refresh,
      error: meQ.error ?? currentQ.error,
    };
  }, [
    signedIn,
    meQ.data,
    meQ.isPending,
    meQ.isError,
    meQ.error,
    currentQ.data,
    currentQ.isPending,
    currentQ.isError,
    currentQ.error,
    tenantId,
    switchTenant,
    refresh,
  ]);

  // Recover from a stale stored tenant id.
  useEffect(() => {
    if (isApiError(currentQ.error) && currentQ.error.status === 404 && preferred) {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      setPreferred(null);
    }
  }, [currentQ.error, preferred]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
}

/** `const canSegment = useFeature('segments')` */
export function useFeature(key: FeatureKey) {
  return useTenant().hasFeature(key);
}

/** `const showGoogle = useFeatureOnPlatform('google.business')` (see `isOnPlatform`). */
export function useFeatureOnPlatform(key: FeatureKey) {
  return useTenant().isOnPlatform(key);
}
